import React, { useState } from 'react';
import { useParams } from 'react-router';
import Button from '@mui/material/Button';
import { Alert, Box, Grid, Card, CardMedia, CardContent, Typography, IconButton, Stack } from '@mui/material';
import useSnackbar from '../../hooks/useSnackbar';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCustomColourModal from './modal/AddCustomColourModal';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';
import { usePutMutation } from '../../hooks/useAuthenticatedMutation';

const hexToRgb = (hex) => {
    hex = hex.replace(/^#/, '');
    const bigint = parseInt(hex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return { r, g, b };
};

const CustomColour = () => {
    const { data: account } = useSuspenseFetch('account');
    const customColours = account.customColours && account.customColours.map(c => ({ id: c.id, customerId: c.customerId, value: c.value, label: c.label }));
    const putMutation = usePutMutation('account');
    const { id } = useParams();
    const [customColourDialog, setCustomColourDialog] = useState(false);
    const { showSnackbar } = useSnackbar();

    const handleOnSubmit = (colour, label) => {
        if (customColours.some(c => c.value === colour)) {
            showSnackbar('Colour already exists', 'error');
            return;
        }

        const payload = {
            customColours: [...customColours, { id: null, customerId: id, value: colour, label: label }]
        };

        const onSuccess = () => {
            showSnackbar('Colour added successfully.', 'success');
            setCustomColourDialog(false);
        };

        putMutation.mutate(payload, { onSuccess });
    };

    const handleDeleteColour = (row) => {
        const payload = {
            customColours: customColours.filter(colour => colour.id !== row.id)
        };

        const onSuccess = () => {
            showSnackbar('Colour deleted successfully.', 'success');
            setCustomColourDialog(false);
        };

        putMutation.mutate(payload, { onSuccess });
    };

    return (
        <>
            <Button variant="contained" onClick={() => setCustomColourDialog(true)}>Add Brand Colour</Button>
            {customColours.length > 0 ? (
                <Box sx={{ flexGrow: 1, mt: 2, width: '100%' }}>
                    <Grid
                        container
                        spacing={2}
                    >
                        {customColours.map((colour, index) => {
                            const rgb = hexToRgb(colour.value);
                            return (
                                <Grid
                                    size={{ xs: 12, sm: 6, md: 3 }}
                                    key={colour.id ?? index}
                                >
                                    <Card
                                        sx={{
                                            borderRadius: 5,
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }}
                                    >
                                        <CardMedia
                                            sx={{
                                                backgroundColor: colour.value,
                                                borderTopLeftRadius: 8,
                                                borderTopRightRadius: 8,
                                                aspectRatio: '16 / 9'
                                            }}
                                        />

                                        <CardContent sx={{ pt: 1 }}>
                                            <Stack direction="row" alignItems="start" spacing={1}>
                                                <Typography
                                                    variant="h6"
                                                    title={colour.label ?? ''}
                                                    sx={{
                                                        flexGrow: 1,
                                                        wordBreak: 'break-word'
                                                    }}
                                                >
                                                    {colour.label ?? ''}
                                                </Typography>

                                                <IconButton
                                                    onClick={() => handleDeleteColour(colour)}
                                                    size="small"
                                                    disabled={putMutation.isPending}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>

                                            <Stack spacing={0.2}>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    HEX: {colour.value}
                                                </Typography>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    RGB: ({rgb.r}, {rgb.g}, {rgb.b})
                                                </Typography>
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>
            ) : (
                <Alert sx={{ mt: 2 }} severity="info">No brand colours available.</Alert>
            )}
            {customColourDialog && (
                <AddCustomColourModal
                    shown={customColourDialog}
                    onHide={() => setCustomColourDialog(false)}
                    onSubmit={handleOnSubmit}
                    onLoading={putMutation.isPending}
                />
            )}
        </>
    );
};

export default CustomColour;